import { Component, Input, Output, EventEmitter, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { I18nService } from '../services/i18n.service';
import { SettingsService } from '../services/settings.service';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <!-- Mobile Overlay -->
    @if (isOpen) {
    <div 
      class="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
      (click)="toggle()"
    ></div>
    }

    <!-- Sidebar -->
    <aside 
      class="fixed inset-y-0 left-0 w-20 sidebar-bg text-white flex flex-col justify-between items-center py-8 transition-transform duration-300 z-50 overflow-hidden"
      [class.-translate-x-full]="!isOpen && isMobile"
      [class.translate-x-0]="isOpen || !isMobile"
    >
      <!-- Top Section (Logo/App Icon) -->
      <div class="flex flex-col items-center gap-8">
        <div class="w-12 h-12 bg-primary rounded-xl flex items-center justify-center sidebar-logo-glow">
          <svg xmlns="http://www.w3.org/2000/svg" class="w-7 h-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m8 3 4 8 5-5 5 15H2L8 3z"/></svg>
        </div>
        
        <!-- Navigation Icons -->
        <nav class="flex flex-col gap-6">
          <a routerLink="/dashboard" routerLinkActive="sidebar-item-active" [routerLinkActiveOptions]="{exact: true}" 
             class="sidebar-item group">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/></svg>
            <span class="sidebar-tooltip">{{ i18n.t('sidebar.dashboard') }}</span>
          </a>

          <!-- Staff Management -->
          <a routerLink="/users" routerLinkActive="sidebar-item-active"
             class="sidebar-item group">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M11.5 10c0 1.5-1 3.5-2.5 3.5S6.5 11.5 6.5 10"/><circle cx="19" cy="11" r="3"/><path d="M19 8v1"/><path d="M19 13v1"/><path d="m21.6 9.5-.86.5"/><path d="m17.26 12-.86.5"/><path d="m21.6 12.5-.86-.5"/><path d="m17.26 10-.86-.5"/></svg>
            <span class="sidebar-tooltip">{{ settingsService.settingsSubject.value.staffLabel || i18n.t('sidebar.users') }}</span>
          </a>

          <!-- Customer Management -->
          <a routerLink="/customers" routerLinkActive="sidebar-item-active"
             class="sidebar-item group">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            <span class="sidebar-tooltip">{{ settingsService.settingsSubject.value.customerLabel || i18n.t('customers.title') }}</span>
          </a>

          <!-- Roles Management -->
          @if (settings.rolesEnabled) {
          <a routerLink="/roles" routerLinkActive="sidebar-item-active"
             class="sidebar-item group">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="M12 8v4"/><path d="M12 16h.01"/></svg>
            <span class="sidebar-tooltip">{{ i18n.t('sidebar.roles') }}</span>
          </a>
          }

          <!-- Permissions Management -->
          @if (settings.rolesEnabled) {
          <a routerLink="/permissions" routerLinkActive="sidebar-item-active"
             class="sidebar-item group">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            <span class="sidebar-tooltip">{{ i18n.t('sidebar.permissions') }}</span>
          </a>
          }
        </nav>
      </div>

      <!-- Bottom Section -->
      <div class="flex flex-col items-center gap-6 mt-auto">
        <!-- Assets Manager -->
        <button 
          (click)="onOpenAssets.emit(); isMobile && toggle()"
          class="sidebar-item group"
          [title]="i18n.t('sidebar.assets')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 10V7a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"/><path d="M12 12V7"/><path d="m16 16-4-4-4 4"/></svg>
          <span class="sidebar-tooltip">{{ i18n.t('sidebar.assets') }}</span>
        </button>

        <!-- Project Settings -->
        <button 
          (click)="onOpenSettings.emit(); isMobile && toggle()"
          class="sidebar-item group"
          [title]="i18n.t('sidebar.settings')"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"/><circle cx="12" cy="12" r="3"/></svg>
          <span class="sidebar-tooltip">{{ i18n.t('sidebar.settings') }}</span>
        </button>

        <!-- User Profile -->
        <button 
          (click)="onOpenUser.emit(); isMobile && toggle()"
          class="w-12 h-12 rounded-full overflow-hidden border-2 transition-all group relative bg-primary/10 flex items-center justify-center sidebar-item"
          style="border-color: rgba(var(--primary-color-rgb), 0.4)"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          <span class="sidebar-tooltip">{{ i18n.t('sidebar.profile') }}</span>
        </button>
      </div>
    </aside>

    <!-- Mobile Toggle Button -->
    @if (isMobile) {
    <button 
      (click)="toggle()"
      class="fixed bottom-6 right-6 w-14 h-14 bg-primary text-white rounded-full flex items-center justify-center z-50 lg:hidden hover:scale-110 active:scale-95 transition-transform shadow-2xl"
      style="box-shadow: 0 8px 24px rgba(var(--primary-color-rgb), 0.45)"
    >
      @if (!isOpen) {
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="4" x2="20" y1="12" y2="12"/><line x1="4" x2="20" y1="6" y2="6"/><line x1="4" x2="20" y1="18" y2="18"/></svg>
      }
      @if (isOpen) {
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" x2="6" y1="6" y2="18"/><line x1="6" x2="18" y1="6" y2="18"/></svg>
      }
    </button>
    }
  `,
  styles: [`
    :host { display: block; }
  `]
})
export class SidebarComponent implements OnInit {
  @Input() isOpen = false;
  @Input() isMobile = false;

  @Output() isOpenChange = new EventEmitter<boolean>();
  @Output() onOpenSettings = new EventEmitter<void>();
  @Output() onOpenUser = new EventEmitter<void>();
  @Output() onOpenAssets = new EventEmitter<void>();

  settings: any;

  constructor(
    public i18n: I18nService,
    public settingsService: SettingsService,
    private cdr: ChangeDetectorRef
  ) {
    this.settings = settingsService.settingsSubject.value;
  }

  ngOnInit() {
    this.settingsService.settings$.subscribe(s => {
      this.settings = s;
      this.cdr.markForCheck();
    });
  }

  toggle() {
    this.isOpen = !this.isOpen;
    this.isOpenChange.emit(this.isOpen);
  }
}
